<template>
    <b-modal :visible="visible" title="Dossier médical — Avis" size="xl" scrollable centered
        modal-class="avis-dossier-modal" :no-close-on-backdrop="saving" :no-close-on-esc="saving"
        :hide-header-close="saving" @change="$emit('visibilite', $event)" @hidden="nettoyer">
        <p v-if="loading" role="status">Chargement du dossier…</p>
        <div v-else-if="error" class="alert alert-danger" role="alert">{{ error }}
            <button type="button" class="btn btn-secondary-light-o" @click="charger">Réessayer</button>
        </div>
        <template v-else-if="dossier">
            <header class="avis-dossier-identite">
                <div><strong>{{ dossier.patient.nom }} {{ dossier.patient.prenoms }}</strong>
                    <span v-if="dossier.patient.code"> · {{ dossier.patient.code }}</span></div>
                <small>Dossier en lecture seule · Consultation n° {{ dossier.consultation_id }}</small>
            </header>
            <section class="avis-dossier-section" aria-label="Avis">
                <h2>Avis · {{ dossier.avis.specialite_nom }}</h2>
                <p v-if="replyError" class="alert alert-danger" role="alert">{{ replyError }}</p>
                <AvisReponse :avis="dossier.avis" :value="value" :champ-id="'avis-dossier-reponse-' + avisId"
                    :disabled="saving" :saving="saving" @input="$emit('input', $event)" @envoyer="$emit('envoyer')" />
            </section>
            <section class="avis-dossier-section">
                <h2>Antécédents et informations du patient</h2>
                <p v-if="!dossier.antecedents.length">Aucun antécédent renseigné.</p>
                <ul v-else class="avis-antecedents"><li v-for="item in dossier.antecedents" :key="item.id">
                    <strong>{{ item.categorie }} · {{ item.champ }} :</strong>
                    {{ item.contenu || (Number(item.est_indisponible) ? 'Non renseigné' : 'Aucun élément signalé') }}
                </li></ul>
            </section>
            <section class="avis-dossier-section">
                <h2>Notes et historique clinique</h2>
                <p class="small">Consultations, évolutions, prescriptions et documents enregistrés dans ce dossier.</p>
                <details v-for="consultation in dossier.consultations" :key="consultation.id"
                    :open="Number(consultation.id) === Number(dossier.consultation_id)">
                    <summary>{{ consultation.specialite || 'Consultation' }} · {{ consultation.reference_bon || ('N° ' + consultation.id) }}
                        <small>{{ date(consultation.created_at) }}</small></summary>
                    <p v-if="!notes(consultation.id).length">Aucune note enregistrée pour cette consultation.</p>
                    <article v-for="note in notes(consultation.id)" :key="note.id" class="avis-note">
                        <small>{{ date(note.date) }} <span v-if="note.evolution_id"> · Évolution n° {{ note.evolution_id }}</span></small>
                        <div class="avis-note-contenu" v-html="rendre(note)"></div>
                    </article>
                    <article v-for="autre in autresAvis(consultation.id)" :key="'avis-' + autre.id" class="avis-note">
                        <h3>Avis · {{ autre.specialite_nom }}</h3>
                        <p class="avis-texte"><strong>Demande :</strong> {{ autre.question }}</p>
                        <p class="avis-texte"><strong>Réponse :</strong> {{ autre.reponse || 'En attente de réponse' }}</p>
                    </article>
                </details>
            </section>
        </template>
        <template #modal-footer>
            <button type="button" class="btn btn-secondary-light-o" :disabled="saving" @click="$emit('visibilite', false)">Fermer</button>
        </template>
    </b-modal>
</template>
<script>
import { BModal } from 'bootstrap-vue'
import consultations from '@/store/mods/consultations/consultations'
import AvisReponse from './avis-reponse.vue'
import { historique_lecture } from '@/utils/resume-consultation-print'
import { parseBackendTimestamp } from '@/utils/backend-timestamp'

export default {
    name: 'AvisDossier', components: { BModal, AvisReponse },
    props: {
        visible: { type: Boolean, default: false }, avisId: { type: Number, default: 0 },
        value: { type: String, default: '' }, saving: { type: Boolean, default: false },
        replyError: { type: String, default: '' }
    },
    data() { return { dossier: null, loading: false, error: '', generation: 0 } },
    watch: {
        visible(value) { if (value) this.charger(); else this.nettoyer() },
        avisId() { if (this.visible) this.charger() }
    },
    beforeDestroy() { this.nettoyer() },
    methods: {
        nettoyer() { this.generation++; this.dossier = null; this.error = ''; this.loading = false },
        async charger() {
            const generation = ++this.generation
            const id = this.avisId
            this.dossier = null; this.error = ''; this.loading = true
            try {
                const access = await consultations.getTableauMedecinAccess()
                if (!access || access.success !== true || !access.data || !access.data.access_token) throw new Error()
                const result = await consultations.getDossierAvisMedical(id, access.data.access_token)
                if (generation !== this.generation || this._isDestroyed || !this.visible) return
                if (!result || !result.data || !result.data.patient || Number(result.data.avis.id) !== id) throw new Error()
                this.dossier = result.data
            } catch (error) {
                if (generation === this.generation && !this._isDestroyed) this.error =
                    'Dossier indisponible. Vérifiez votre accès ou réessayez.'
            } finally {
                if (generation === this.generation && !this._isDestroyed) this.loading = false
            }
        },
        notes(id) { return this.dossier.historiques.filter(note => Number(note.consultation_id) === Number(id)) },
        autresAvis(id) { return this.dossier.autres_avis.filter(avis => Number(avis.consultation_id) === Number(id) && Number(avis.id) !== this.avisId) },
        rendre: historique_lecture,
        date(value) {
            if (!value) return ''
            const date = parseBackendTimestamp(value)
            return isNaN(date.getTime()) ? '' : date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone: this.getClinicTimezone() })
        }
    }
}
</script>
<style lang="scss">
.avis-dossier-modal + .modal-backdrop {
    background: rgba(20, 29, 46, .35) !important;
    opacity: 1 !important;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
}
.avis-dossier-modal {
    .modal-content { border-radius: 22px; overflow: hidden; color: var(--ui-text, #172033); }
    .modal-body { background: #f5f8ff; padding: 20px; }
    .modal-footer { background: #fff; }
    .avis-dossier-identite { background: #eaf1ff; padding: 16px; border-radius: 14px; margin-bottom: 16px; }
    .avis-dossier-identite strong { font-size: 18px; }
    .avis-dossier-identite small { display: block; margin-top: 4px; }
    .avis-dossier-section { padding: 18px; background: #fff; border: 1px solid #dce4f1; border-radius: 16px; margin-bottom: 16px; }
    h2 { font-size: 19px; font-weight: 700; margin-bottom: 14px; }
    h3, .rubrique h2 { font-size: 16px; font-weight: 600; }
    summary { cursor: pointer; padding: 12px 0; font-weight: 600; }
    summary small { font-weight: 400; margin-left: 8px; }
    details + details, .avis-note + .avis-note { border-top: 1px solid #dce4f1; }
    .avis-note { padding: 12px 0; }
    .avis-note-contenu { overflow-x: auto; overflow-wrap: anywhere; margin-top: 8px; }
    .avis-note-contenu table { width: 100%; border-collapse: collapse; }
    .avis-note-contenu th, .avis-note-contenu td { border: 1px solid #dce4f1; padding: 8px; text-align: left; }
    .avis-note-contenu th { background: #f2f5fc; }
    .avis-texte { white-space: pre-wrap; overflow-wrap: anywhere; }
    .avis-antecedents { padding-left: 20px; }
    .avis-antecedents li { margin: 8px 0; }
    @media (max-width: 575px) { .modal-body { padding: 12px; } .avis-dossier-section { padding: 12px; } }
}
</style>
